import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useCreateTeamShare, useTeamMembers } from '@/api/teams';
import { type AvailableService, serviceProviderScope } from '@/api/services';
import { useMyProjects } from '@/api/projects';
import { categoryOfScope } from '@/pages/services/categories';
import { PermissionCheckboxes } from './PermissionCheckboxes';
import { ServiceSelect } from '@/components/service-select/ServiceSelect';
import { DomainMultiSelect } from './DomainMultiSelect';

interface AddPermissionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string;
  /** Team's organization — projects are filtered to the same org for the scope picker. */
  organization?: string;
}

const ORG_WIDE = 'org-wide';
// Sentinel: empty string = all members (avoids Radix Select empty-string edge cases via comparison).
const ALL_MEMBERS = '__all__';
// Empty preset — force a deliberate scope choice rather than defaulting to the
// broadest grant (least privilege: org-wide applies across every project).
const NO_SCOPE = '';

export function AddPermissionDialog({
  open,
  onOpenChange,
  teamId,
  organization,
}: AddPermissionDialogProps) {
  const { show } = useToast();
  const { data: projects = [] } = useMyProjects();
  const { data: members = [] } = useTeamMembers(teamId);

  const [service, setService] = useState<AvailableService | null>(null);
  const [actions, setActions] = useState<string[]>([]);
  const [scope, setScope] = useState<string>(NO_SCOPE);
  const [memberId, setMemberId] = useState<string>(ALL_MEMBERS);
  const [domainIds, setDomainIds] = useState<string[]>([]);

  const scopeProjects = useMemo(
    () =>
      organization
        ? projects.filter((p) => p.organization_id === organization)
        : projects,
    [projects, organization],
  );

  // Active (non-pending) members for the "Applies to" picker.
  const activeMembers = useMemo(
    () => members.filter((m) => m.active && !m.pending),
    [members],
  );

  // A CDN service is one whose provider legacy_scope maps to the 'cdn' category.
  // This uses the same CDN_SCOPES set that powers categoryOfScope / the services dashboard.
  const isCDN = useMemo(
    () => !!service && categoryOfScope(serviceProviderScope(service)) === 'cdn',
    [service],
  );

  // Clear domain selection whenever the service changes so stale IDs don't leak.
  useEffect(() => {
    setDomainIds([]);
  }, [service?.id]);

  // Cache invalidation is handled inside useCreateTeamShare; no onSuccess/onError
  // callbacks here so the multi-call case can control toasts + close centrally.
  const create = useCreateTeamShare(teamId);

  function reset() {
    setService(null);
    setActions([]);
    setScope(NO_SCOPE);
    setMemberId(ALL_MEMBERS);
    setDomainIds([]);
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  async function handleSubmit() {
    if (!service || actions.length === 0 || !scope) return;

    const base = {
      service_id: service.id,
      actions,
      project_id: scope === ORG_WIDE ? undefined : scope,
      member_id: memberId === ALL_MEMBERS ? undefined : memberId,
    };

    try {
      if (isCDN && domainIds.length > 0) {
        // One share per domain; collect results to report partial failures gracefully.
        const results = await Promise.allSettled(
          domainIds.map((domainId) =>
            create.mutateAsync({ ...base, resource_type: 'domain', resource_id: domainId }),
          ),
        );
        const succeeded = results.filter((r) => r.status === 'fulfilled').length;
        const failed = results.length - succeeded;
        if (succeeded > 0) {
          show({ tone: 'success', title: `${succeeded} permission${succeeded !== 1 ? 's' : ''} added` });
        }
        if (failed > 0) {
          show({ tone: 'danger', title: `${failed} of ${results.length} could not be added` });
        }
      } else {
        await create.mutateAsync(base);
        show({ tone: 'success', title: 'Permission added' });
      }
      onOpenChange(false);
    } catch {
      show({ tone: 'danger', title: 'Could not add permission' });
    }
  }

  const canSubmit = !!service && actions.length > 0 && !!scope && !create.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Add permission</DialogTitle>
          <DialogDescription>
            Grant this team access to a service and choose what they can do.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 20 }}>
            <Field
              label="Service"
              required
              description="Search across all services in your organization."
            >
              {() => <ServiceSelect selected={service} onSelect={setService} />}
            </Field>

            <Field label="Permissions" required>
              {() => <PermissionCheckboxes value={actions} onChange={setActions} />}
            </Field>

            <Field
              label="Scope"
              required
              description="Choose a project to scope this grant, or organization-wide to apply across all projects."
            >
              {() => (
                <Select value={scope} onValueChange={setScope}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a scope" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ORG_WIDE}>Organization-wide (All Projects)</SelectItem>
                    {scopeProjects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field
              label="Applies to"
              description="Restrict this grant to a specific team member, or apply to all members."
            >
              {() => (
                <Select value={memberId} onValueChange={setMemberId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_MEMBERS}>All team members</SelectItem>
                    {activeMembers.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            {isCDN && (
              <Field
                label="Specific domains"
                description="Leave empty to grant all domains in this service, or pick specific domains to restrict access."
              >
                {() => <DomainMultiSelect value={domainIds} onChange={setDomainIds} />}
              </Field>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={handleSubmit}>
            {create.isPending ? 'Adding…' : 'Add permission'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
